"use client"

import { useEffect, useRef, useState } from "react"
import clsx from "clsx"

import type { UnlikelyProduct } from "@unlikelystudio/commerce-connector"

import type { PropsWithClassName } from "~/@types/generics"
import { productMetafields } from "~/lib/shopify/constants"
import { getLocalizedMetafields } from "~/lib/shopify/utils/get-localized-metafields"
import useLocale from "~/hooks/useLocale"
import HtmlRichText from "~/components/ui/HtmlRichText"
import InlineCta from "~/components/ui/InlineCta"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"
import { metafieldsToObject } from "~/utils/metafields-to-object"

import * as css from "./styles.css"

export function ProductDescription({ className, product }: PropsWithClassName<{ product: UnlikelyProduct }>) {
  const locale = useLocale()
  const t = useTranslate()

  const descriptionRef = useRef<HTMLDivElement>(null)
  const [shouldTruncateDescription, setShouldTruncateDescription] = useState(false)
  const [truncateDescription, setTruncateDescription] = useState(true)

  useEffect(() => {
    if (descriptionRef?.current) {
      const isTruncated =
        Number(descriptionRef?.current?.clientHeight) > 0 &&
        descriptionRef.current.scrollHeight > descriptionRef?.current?.clientHeight

      // Check if the line clamping is active
      setTruncateDescription(isTruncated)
      return setShouldTruncateDescription(isTruncated)
    } else {
      setShouldTruncateDescription(false)
      setTruncateDescription(false)
    }
  }, [descriptionRef.current])

  const productRef = metafieldsToObject(
    product?.metafields ?? {},
    getLocalizedMetafields(productMetafields, locale)
  )?.ref

  const description = product?.descriptionHtml
  const descriptionWithoutDash = description?.replace(/<li>\s*-[^<]*<\/li>/gm, (match) => match.replace("-", ""))

  return (
    <div className={clsx(css.ProductDescription, className)}>
      <div className={clsx(css.title)}>{t("product_description_title")}</div>
      <div className={clsx(css.descriptionContainer({ truncate: truncateDescription }))} ref={descriptionRef}>
        <div className={clsx(css.description)}>
          <HtmlRichText>{descriptionWithoutDash}</HtmlRichText>
          {productRef && <div className={css.productRef}>{`${t("description_ref_title")} : ${productRef}`}</div>}
        </div>
      </div>
      {shouldTruncateDescription &&
        (truncateDescription ? (
          <InlineCta
            className={css.truncatedCta}
            color={"ui-900"}
            withLine
            withHover={false}
            withDefaultLine
            size="small"
            onClick={() => setTruncateDescription(false)}
          >
            {t("cta_read_more")}
          </InlineCta>
        ) : (
          <InlineCta
            className={css.truncatedCta}
            color={"ui-900"}
            withLine
            withHover={false}
            withDefaultLine
            size="small"
            onClick={() => setTruncateDescription(true)}
          >
            {t("cta_read_less")}
          </InlineCta>
        ))}
    </div>
  )
}
